import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
	ArrowRight,
	BarChart3,
	Bike,
	Building2,
	CalendarClock,
	Check,
	ChevronDown,
	ClipboardList,
	Clock,
	FileCode2,
	FileSpreadsheet,
	Layers,
	MapPin,
	MapPinned,
	Phone,
	Plug,
	Route,
	Send,
	Shuffle,
	Smartphone,
	Store,
	Truck,
	Users,
} from 'lucide-react';
import { cn } from '@/utils/merge';
import {
	BRANDS,
	CITIES,
	OWN_BRAND,
	CONTACT,
	INTEGRATION_KINDS,
	INTEGRATION_LOGOS,
	ROLES,
	SCREENSHOTS,
	STATS,
	sendInterest,
} from './content';
import { Action, BrowserFrame, Container, Eyebrow, PhoneFrame, Reveal, SectionHeading } from './ui';
import { useInterest } from './interest';

/* ── Hero ─────────────────────────────────────────────────────────────── */

// `showcase` replaces the picture on the right (the /3d preview's city).
export const Hero = ({ showcase } = {}) => (
	<section
		id='arxiki'
		aria-labelledby='hero-title'
		className='relative overflow-hidden pt-[146px] pb-16 sm:pt-[166px] sm:pb-24'
	>
		<div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-0'>
			<div className='absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-[radial-gradient(closest-side,rgba(22,119,255,0.14),transparent)]' />
			<div className='absolute top-60 -left-40 h-[420px] w-[420px] rounded-full bg-[radial-gradient(closest-side,rgba(255,107,44,0.10),transparent)]' />
		</div>

		<Container className='relative grid items-center gap-14 lg:grid-cols-[1.02fr_1fr] lg:gap-10'>
			<div className='flex flex-col items-start'>
				<Eyebrow>
					<span className='h-1.5 w-1.5 animate-pulse rounded-full bg-(--dm-blue)' />
					Πλατφόρμα διαχείρισης delivery
				</Eyebrow>

				<h1
					id='hero-title'
					className='mt-6 text-[40px] leading-[1.05] font-extrabold sm:text-[56px] lg:text-[62px]'
				>
					Η τεχνολογία που κινεί το{' '}
					<span className='relative whitespace-nowrap text-(--dm-blue)'>
						delivery
						<svg
							aria-hidden='true'
							viewBox='0 0 200 12'
							preserveAspectRatio='none'
							className='absolute -bottom-1.5 left-0 h-3 w-full text-(--dm-orange)'
						>
							<path
								d='M2 9 C 50 3, 150 3, 198 8'
								stroke='currentColor'
								strokeWidth='4'
								fill='none'
								strokeLinecap='round'
							/>
						</svg>
					</span>{' '}
					της πόλης σου.
				</h1>

				<p className='mt-6 max-w-xl text-lg leading-relaxed text-(--dm-muted) sm:text-xl'>
					Ολοκληρωμένη πλατφόρμα διαχείρισης delivery για καλύτερη οργάνωση, περισσότερες
					παραγγελίες και ανάπτυξη της επιχείρησής σου.
				</p>

				<HeroActions />

				<ul className='mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium text-(--dm-muted)'>
					{[
						{ icon: MapPin, text: `Σε λειτουργία σε ${CITIES.length} πόλεις` },
						{ icon: Smartphone, text: 'Εφαρμογή για διανομείς' },
						{ icon: Plug, text: 'Ανοιχτό API' },
					].map(({ icon: Icon, text }) => (
						<li key={text} className='flex items-center gap-2'>
							<Icon size={16} className='text-(--dm-blue)' aria-hidden='true' />
							{text}
						</li>
					))}
				</ul>
			</div>

			{showcase || <HeroShowcase />}
		</Container>
	</section>
);

const HeroActions = () => {
	const { openInterest } = useInterest();
	return (
		<div className='mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row'>
			<Action
				href='#endiaferon'
				onClick={e => {
					e.preventDefault();
					openInterest('fleet');
				}}
				className='min-h-14 px-7 text-base'
			>
				Κλείσε Demo
				<ArrowRight size={18} aria-hidden='true' />
			</Action>
			<Action href='#lyseis' variant='secondary' className='min-h-14 px-7 text-base'>
				Δες πώς λειτουργεί
			</Action>
		</div>
	);
};

const HeroShowcase = () => (
	<div className='relative mx-auto w-full max-w-[600px] lg:max-w-none'>
		<BrowserFrame
			src={SCREENSHOTS.dashboard}
			alt='Το dashboard του DeliveryMan με τις ενεργές παραγγελίες και τον χάρτη'
			label='Screenshot του dashboard'
			eager
			className='ml-auto w-[92%] sm:w-[88%]'
		/>
		<PhoneFrame
			src={SCREENSHOTS.riderApp}
			alt='Η εφαρμογή διανομέα του DeliveryMan'
			label='Screenshot της εφαρμογής διανομέα'
			eager
			className='absolute -bottom-10 left-0 w-[27%] max-w-[160px] sm:-bottom-14'
		/>
	</div>
);

/* ── Who it's for: one section, three tabs ────────────────────────────── */

// Each audience has its own tab; the header's «Για καταστήματα / διανομείς /
// στόλους» links (#katastimata, #dianomeis, #stoloi) land here and open theirs.
const AUDIENCES = [
	{
		id: 'katastimata',
		role: 'shop',
		icon: Store,
		tab: 'Κατάστημα',
		kicker: 'Για καταστήματα',
		title: 'Έχεις κατάστημα; Εμείς φέρνουμε τους διανομείς.',
		text: 'Χρειάζεσαι διανομείς για τις παραδόσεις του καταστήματός σου; Στις πόλεις όπου λειτουργούμε, αναλαμβάνουμε τη διανομή και εσύ βλέπεις κάθε παραγγελία σε πραγματικό χρόνο.',
		points: [
			'Καταχωρείς την παραγγελία σε δευτερόλεπτα',
			'Βλέπεις ποιος διανομέας την έχει και πού βρίσκεται',
			'Ιστορικό παραγγελιών και χρεώσεων σε ένα σημείο',
			'Οι παραγγελίες από άλλα συστήματα έρχονται αυτόματα',
		],
		steps: [
			'Συμπληρώνεις τη φόρμα ή μας καλείς',
			'Σου δίνουμε πρόσβαση στην πλατφόρμα',
			'Στέλνεις παραγγελίες και έρχεται διανομέας',
		],
		cta: 'Ζήτησε εξυπηρέτηση',
		visual: 'browser',
		shot: SCREENSHOTS.live,
		shotAlt: 'Οι παραγγελίες στην οθόνη Live του DeliveryMan',
	},
	{
		id: 'dianomeis',
		role: 'rider',
		icon: Bike,
		tab: 'Διανομέας',
		kicker: 'Για διανομείς',
		title: 'Δούλεψε ως διανομέας, με τους δικούς σου ρυθμούς.',
		text: 'Δούλεψε με ευελιξία και επίλεξε τις βάρδιες που σε εξυπηρετούν. Όλη η δουλειά γίνεται από την εφαρμογή στο κινητό σου.',
		points: [
			'Επιλέγεις τις βάρδιες που σε εξυπηρετούν',
			'Οι παραγγελίες έρχονται με ειδοποίηση στο κινητό',
			'Διεύθυνση, πελάτης και οδηγίες σε μία οθόνη',
			'Το ιστορικό και τα έσοδά σου πάντα μπροστά σου',
		],
		steps: [
			'Κάνεις αίτηση με τη φόρμα',
			'Σε καλούμε για να γνωριστούμε',
			'Ξεκινάς με την εφαρμογή στο κινητό σου',
		],
		cta: 'Κάνε αίτηση',
		visual: 'phone',
		shot: SCREENSHOTS.riderApp,
		shotAlt: 'Η εφαρμογή διανομέα του DeliveryMan',
	},
	{
		id: 'stoloi',
		role: 'fleet',
		icon: Truck,
		tab: 'Στόλος',
		kicker: 'Για στόλους & συνεργάτες',
		title: 'Έχεις στόλο ή θέλεις να στήσεις delivery στην πόλη σου;',
		text: 'Χρησιμοποίησε την τεχνολογία του DeliveryMan ή συνεργάσου μαζί μας για να αναπτύξεις το delivery στην περιοχή σου, ακόμη και με το δικό σου όνομα, «powered by DeliveryMan».',
		points: [
			'Live χάρτης με διανομείς και παραγγελίες',
			'Αυτόματες αναθέσεις στον καταλληλότερο διανομέα',
			'Στατιστικά ανά κατάστημα, διανομέα και περίοδο',
			'Πολλές πόλεις από έναν λογαριασμό',
		],
		steps: [
			'Κλείνεις μια παρουσίαση',
			'Στήνουμε μαζί την πόλη σου',
			'Ξεκινάς με Live, αναθέσεις και στατιστικά',
		],
		note: {
			title: 'Έχεις ήδη στόλο και δουλεύεις με άλλη εφαρμογή;',
			text: 'Δεν χρειάζεται να ξεκινήσεις από την αρχή. Σε βοηθάμε να περάσεις στην πλατφόρμα του DeliveryMan και συνεχίζεις με το δικό σου όνομα, τους δικούς σου διανομείς και τα καταστήματά σου, ως συνεργάτης «powered by DeliveryMan». Όπως ήδη η Solvd στη Θεσσαλονίκη και η AF Delivery Services στη Φλώρινα.',
		},
		cta: 'Ζήτησε παρουσίαση',
		secondary: 'Γίνε συνεργάτης',
		visual: 'browser',
		shot: SCREENSHOTS.dashboard,
		shotAlt: 'Το dashboard του DeliveryMan με τις ενεργές παραγγελίες και τον χάρτη',
	},
];

const tabForHash = hash => AUDIENCES.findIndex(x => `#${x.id}` === hash);

export const AudienceCards = () => {
	const { openInterest } = useInterest();
	const { hash } = useLocation();
	// Opened on the tab the URL names (/#dianomeis from another page).
	const [active, setActive] = useState(() => Math.max(tabForHash(hash), 0));
	// On this page, a link to #katastimata / #dianomeis / #stoloi (header,
	// footer) opens its tab — even when the URL already has that hash, which a
	// hash-based check would miss.
	useEffect(() => {
		const onClick = e => {
			const link = e.target.closest?.('a[href*="#"]');
			const i = link ? tabForHash('#' + link.getAttribute('href').split('#')[1]) : -1;
			if (i >= 0) setActive(i);
		};
		document.addEventListener('click', onClick);
		return () => document.removeEventListener('click', onClick);
	}, []);
	const a = AUDIENCES[active];

	const onTabKey = e => {
		const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
		if (!step) return;
		e.preventDefault();
		const next = (active + step + AUDIENCES.length) % AUDIENCES.length;
		setActive(next);
		document.getElementById(`tab-${AUDIENCES[next].id}`)?.focus();
	};

	return (
		<section aria-labelledby='audiences-title' className='relative pt-10 pb-20 sm:pt-16 sm:pb-28'>
			{/* Landing spots for the header links. */}
			{AUDIENCES.map(x => (
				<span key={x.id} id={x.id} className='absolute top-0' aria-hidden='true' />
			))}
			<Container>
				<SectionHeading
					id='audiences-title'
					eyebrow='Για ποιον είναι'
					title='Βρες τι σου ταιριάζει.'
					text='Κατάστημα, διανομέας ή στόλος: διάλεξε για να δεις τι κάνει το DeliveryMan για σένα.'
					className='sm:mb-10'
				/>

				<div
					role='tablist'
					aria-label='Για ποιον είναι το DeliveryMan'
					className='mx-auto mb-8 grid max-w-2xl grid-cols-3 gap-1.5 rounded-2xl border border-(--dm-line) bg-white p-1.5 shadow-(--dm-shadow) sm:mb-10'
					onKeyDown={onTabKey}
				>
					{AUDIENCES.map((x, i) => (
						<button
							key={x.id}
							id={`tab-${x.id}`}
							type='button'
							role='tab'
							aria-selected={i === active}
							aria-controls={`panel-${x.id}`}
							tabIndex={i === active ? 0 : -1}
							onClick={() => setActive(i)}
							className={cn(
								'flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl px-2 text-[13px] font-semibold transition-all duration-200 sm:flex-row sm:gap-2 sm:text-base',
								i === active
									? 'bg-(--dm-navy) text-white shadow-[0_8px_20px_rgba(18,59,99,0.25)]'
									: 'text-(--dm-muted) hover:bg-(--dm-blue-soft) hover:text-(--dm-navy)',
							)}
						>
							<x.icon size={20} aria-hidden='true' />
							{x.tab}
						</button>
					))}
				</div>

				<div
					key={a.id}
					id={`panel-${a.id}`}
					role='tabpanel'
					aria-labelledby={`tab-${a.id}`}
					className='dm-tab-panel grid items-center gap-10 rounded-[2rem] border border-(--dm-line) bg-white p-6 shadow-(--dm-shadow) sm:p-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14'
				>
					<div>
						<Eyebrow>{a.kicker}</Eyebrow>
						<h3 className='mt-4 text-2xl leading-tight font-extrabold sm:text-[32px]'>{a.title}</h3>
						<p className='mt-4 text-base leading-relaxed text-(--dm-muted) sm:text-lg'>{a.text}</p>

						<ul className='mt-6 grid gap-3 sm:grid-cols-2'>
							{a.points.map(p => (
								<li key={p} className='flex items-start gap-2.5 text-[15px]'>
									<Check
										size={18}
										className='mt-0.5 shrink-0 text-(--dm-blue)'
										aria-hidden='true'
									/>
									{p}
								</li>
							))}
						</ul>

						{a.note && (
							<div className='mt-6 rounded-2xl border border-(--dm-orange)/30 bg-(--dm-orange-soft) p-5'>
								<p className='text-base font-bold text-(--dm-text)'>{a.note.title}</p>
								<p className='mt-1.5 text-[15px] leading-relaxed text-(--dm-text)/80'>
									{a.note.text}
								</p>
							</div>
						)}

						<h4 className='mt-8 text-sm font-semibold tracking-wider text-(--dm-muted) uppercase'>
							Πώς ξεκινάς
						</h4>
						<ol className='mt-3 grid gap-3 sm:grid-cols-3'>
							{a.steps.map((st, i) => (
								<li
									key={st}
									className='flex items-start gap-3 rounded-xl bg-(--dm-bg) p-3.5 text-[14px] leading-snug sm:flex-col sm:gap-2'
								>
									<span className='inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-(--dm-orange) text-sm font-bold text-white'>
										{i + 1}
									</span>
									{st}
								</li>
							))}
						</ol>

						<div className='mt-8 flex flex-col gap-3 sm:flex-row'>
							<Action
								href='#endiaferon'
								onClick={e => {
									e.preventDefault();
									openInterest(a.role);
								}}
							>
								{a.cta}
								<ArrowRight size={18} aria-hidden='true' />
							</Action>
							{a.secondary && (
								<Action
									href='#endiaferon'
									variant='secondary'
									onClick={e => {
										e.preventDefault();
										openInterest(a.role);
									}}
								>
									{a.secondary}
								</Action>
							)}
						</div>
					</div>

					<div className='flex justify-center'>
						{a.visual === 'phone' ? (
							<PhoneFrame
								src={a.shot}
								alt={a.shotAlt}
								label='Screenshot της εφαρμογής διανομέα'
								className='w-[55%] max-w-[240px]'
							/>
						) : (
							<BrowserFrame
								src={a.shot}
								alt={a.shotAlt}
								label='Screenshot της πλατφόρμας'
								className='w-full'
							/>
						)}
					</div>
				</div>
			</Container>
		</section>
	);
};

/* ── Platform / solutions ─────────────────────────────────────────────── */

const SOLUTIONS = [
	{
		icon: ClipboardList,
		title: 'Διαχείριση παραγγελιών',
		text: 'Καταχώρηση από το κατάστημα ή το κέντρο, πρόχειρες παραγγελίες, πλήρες ιστορικό.',
	},
	{
		icon: Users,
		title: 'Διαχείριση διανομέων',
		text: 'Εφαρμογή για τον διανομέα, βάρδιες, διαλείμματα και διαθεσιμότητα με μια ματιά.',
	},
	{
		icon: Shuffle,
		title: 'Έξυπνες αναθέσεις',
		text: 'Αυτόματη ανάθεση στον καταλληλότερο διανομέα, ή επιλογή από τον ίδιο ή από τον διαχειριστή.',
	},
	{
		icon: MapPinned,
		title: 'Live λειτουργία',
		text: 'Ζωντανός χάρτης με τους διανομείς και την κατάσταση κάθε παραγγελίας σε πραγματικό χρόνο.',
	},
	{
		icon: BarChart3,
		title: 'Αναφορές & στατιστικά',
		text: 'Παραγγελίες, χρόνοι και χρεώσεις ανά κατάστημα, διανομέα και περίοδο.',
	},
	{
		icon: Plug,
		title: 'Integrations & API',
		text: 'Οι παραγγελίες από άλλα συστήματα έρχονται αυτόματα στο DeliveryMan.',
		docs: true,
	},
];

const MORE_SOLUTIONS = [
	{ icon: Route, text: 'Χρεώσεις ανά περιοχή ή ανά απόσταση' },
	{ icon: Clock, text: 'Βάρδιες και Clock In διανομέων' },
	{ icon: Building2, text: 'Πολλές πόλεις από έναν λογαριασμό' },
	{ icon: CalendarClock, text: 'Αυτόματο άνοιγμα και κλείσιμο λειτουργίας' },
	{ icon: Layers, text: 'Ρυθμίσεις ξεχωριστά για κάθε πόλη' },
	{ icon: FileSpreadsheet, text: 'Εξαγωγή παραγγελιών σε Excel' },
];

// `liveShowcase` replaces the Live screenshot beside «Όλη η πόλη σε μία
// οθόνη.» (the /3d preview's city).
export const Solutions = ({ liveShowcase } = {}) => {
	const [showMore, setShowMore] = useState(false);
	return (
		<section
			id='lyseis'
			aria-labelledby='lyseis-title'
			className='border-y border-(--dm-line) bg-white py-20 sm:py-28'
		>
			<Container>
				<SectionHeading
					id='lyseis-title'
					eyebrow='Η πλατφόρμα'
					title='Όλα όσα χρειάζεσαι, σε μία πλατφόρμα.'
					text='Από τη στιγμή που μπαίνει η παραγγελία μέχρι την παράδοση και τα στατιστικά του μήνα.'
				/>

				<div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5'>
					{SOLUTIONS.map((f, i) => (
						<Reveal
							as='article'
							key={f.title}
							delay={(i % 3) * 70}
							className='rounded-2xl border border-(--dm-line) bg-(--dm-bg) p-6 transition-colors duration-300 hover:border-(--dm-blue)/40 hover:bg-white sm:p-7'
						>
							<div className='mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white text-(--dm-blue) shadow-sm'>
								<f.icon size={24} aria-hidden='true' />
							</div>
							<h3 className='text-lg font-bold'>{f.title}</h3>
							<p className='mt-2 text-[15px] leading-relaxed text-(--dm-muted)'>{f.text}</p>
							{f.docs && <DocsLink className='mt-3' />}
						</Reveal>
					))}
				</div>

				<div
					id='perissoteres-lyseis'
					hidden={!showMore}
					className='mt-5 rounded-2xl border border-(--dm-line) bg-(--dm-bg) p-6 sm:p-8'
				>
					<h3 className='mb-5 text-lg font-bold'>Και ακόμη</h3>
					<ul className='grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3'>
						{MORE_SOLUTIONS.map(m => (
							<li key={m.text} className='flex items-center gap-3 text-[15px]'>
								<m.icon size={20} className='shrink-0 text-(--dm-blue)' aria-hidden='true' />
								{m.text}
							</li>
						))}
					</ul>
				</div>

				<div className='mt-10 flex justify-center'>
					<Action
						variant='secondary'
						onClick={() => setShowMore(v => !v)}
						aria-expanded={showMore}
						aria-controls='perissoteres-lyseis'
					>
						{showMore ? 'Λιγότερες λύσεις' : 'Δες όλες τις λύσεις'}
						<ChevronDown
							size={18}
							className={cn('transition-transform', showMore && 'rotate-180')}
							aria-hidden='true'
						/>
					</Action>
				</div>

				<Reveal className='mt-16 grid items-center gap-10 sm:mt-20 lg:grid-cols-2'>
					<div>
						<Eyebrow tone='orange'>Live</Eyebrow>
						<h3 className='mt-4 text-2xl leading-tight font-extrabold sm:text-3xl'>
							Όλη η πόλη σε μία οθόνη.
						</h3>
						<p className='mt-4 text-base leading-relaxed text-(--dm-muted) sm:text-lg'>
							Νέες παραγγελίες, διανομείς σε κίνηση και καθυστερήσεις, όλα σε πραγματικό χρόνο. Το
							σύστημα αναθέτει μόνο του ή αφήνει την απόφαση σε σένα.
						</p>
					</div>
					{liveShowcase || (
						<BrowserFrame
							src={SCREENSHOTS.live}
							alt='Η οθόνη Live του DeliveryMan με τον χάρτη των διανομέων'
							label='Screenshot της οθόνης Live'
						/>
					)}
				</Reveal>
			</Container>
		</section>
	);
};

// The public API documentation (/docs), opened in a new tab.
const DocsLink = ({ className }) => (
	<a
		href='/docs'
		target='_blank'
		rel='noopener'
		className={cn(
			'inline-flex items-center gap-1.5 text-[15px] font-semibold text-(--dm-blue) hover:text-(--dm-navy)',
			className,
		)}
	>
		Τεκμηρίωση API
		<ArrowRight size={16} aria-hidden='true' />
	</a>
);

/* ── Integrations ─────────────────────────────────────────────────────── */

export const Integrations = () => (
	<section id='integrations' aria-labelledby='integrations-title' className='py-20 sm:py-28'>
		<Container>
			<div className='grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center'>
				<Reveal>
					<SectionHeading
						id='integrations-title'
						align='left'
						eyebrow='Integrations'
						title='Συνδέσεις & Integrations'
						text='Σύνδεσε το DeliveryMan με τα συστήματα και τις πλατφόρμες που χρησιμοποιεί η επιχείρησή σου.'
						className='mb-8 sm:mb-8'
					/>
					<div className='rounded-2xl border border-(--dm-line) bg-white p-5 shadow-(--dm-shadow)'>
						<p className='text-[15px] leading-relaxed text-(--dm-muted)'>
							Για προγραμματιστές: στείλε παραγγελίες στο DeliveryMan από το δικό σου σύστημα με το
							ανοιχτό μας API.
						</p>
						<Action href='/docs' target='_blank' rel='noopener' variant='navy' className='mt-4'>
							<FileCode2 size={18} aria-hidden='true' />
							Τεκμηρίωση API (Docs)
						</Action>
					</div>
				</Reveal>

				<div className='grid gap-4 sm:grid-cols-2'>
					{INTEGRATION_KINDS.map((k, i) => (
						<Reveal
							key={k.title}
							delay={(i % 2) * 70}
							className='rounded-2xl border border-(--dm-line) bg-white p-6 shadow-(--dm-shadow)'
						>
							<div className='mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-(--dm-blue-soft) text-(--dm-blue)'>
								<Plug size={20} aria-hidden='true' />
							</div>
							<h3 className='text-base font-bold'>{k.title}</h3>
							<p className='mt-1.5 text-[15px] leading-relaxed text-(--dm-muted)'>{k.text}</p>
						</Reveal>
					))}
				</div>
			</div>

			{INTEGRATION_LOGOS.length > 0 && (
				<ul
					aria-label='Συνδεδεμένα συστήματα'
					className='mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-6 border-t border-(--dm-line) pt-10'
				>
					{INTEGRATION_LOGOS.map(l => (
						<li key={l.name}>
							<img
								src={l.logo}
								alt={l.name}
								loading='lazy'
								decoding='async'
								className='h-9 w-auto opacity-80 grayscale transition hover:opacity-100 hover:grayscale-0'
							/>
						</li>
					))}
				</ul>
			)}
		</Container>
	</section>
);

/* ── Stats ────────────────────────────────────────────────────────────── */

const formatStat = (n, exact) => {
	if (exact) return String(n);
	if (n >= 1000000) {
		return `${(n / 1000000).toLocaleString('el-GR', { maximumFractionDigits: 1 })} εκ.+`;
	}
	return `${n.toLocaleString('el-GR')}+`;
};

const StatValue = ({ value, exact, started }) => {
	const [shown, setShown] = useState(value);
	useEffect(() => {
		if (!started) return;
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
		let raf;
		let t0;
		const step = ts => {
			t0 ??= ts;
			const p = Math.min((ts - t0) / 1400, 1);
			const eased = 1 - Math.pow(1 - p, 3);
			setShown(p < 1 ? Math.floor(eased * value) : value);
			if (p < 1) raf = requestAnimationFrame(step);
		};
		raf = requestAnimationFrame(step);
		return () => cancelAnimationFrame(raf);
	}, [started, value]);
	return formatStat(shown, exact);
};

export const Stats = () => {
	const ref = useRef(null);
	const [started, setStarted] = useState(false);
	const items = STATS.filter(s => s.value != null);

	useEffect(() => {
		const el = ref.current;
		if (!el || typeof IntersectionObserver === 'undefined') return;
		const obs = new IntersectionObserver(
			([e]) => {
				if (e.isIntersecting) {
					setStarted(true);
					obs.disconnect();
				}
			},
			{ threshold: 0.4 },
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, []);

	if (!items.length) return null;

	return (
		<section aria-labelledby='stats-title' className='pb-20 sm:pb-28'>
			<Container>
				<div
					ref={ref}
					className='rounded-3xl border border-(--dm-line) bg-white px-6 py-10 shadow-(--dm-shadow) sm:px-10 sm:py-12'
				>
					<h2
						id='stats-title'
						className='mb-8 text-center text-sm font-semibold tracking-wider text-(--dm-muted)! uppercase'
					>
						Το DeliveryMan σε αριθμούς
					</h2>
					<dl
						className={cn(
							'grid gap-8 text-center',
							items.length >= 4
								? 'grid-cols-2 lg:grid-cols-4'
								: items.length === 3
									? 'grid-cols-1 sm:grid-cols-3'
									: 'grid-cols-2',
						)}
					>
						{items.map(s => (
							<div key={s.key} className='flex flex-col-reverse gap-1'>
								<dt className='text-sm font-medium text-(--dm-muted) sm:text-base'>{s.label}</dt>
								<dd className='text-4xl font-extrabold tracking-tight text-(--dm-navy) tabular-nums sm:text-5xl'>
									<StatValue value={s.value} exact={s.exact} started={started} />
								</dd>
							</div>
						))}
					</dl>
				</div>
			</Container>
		</section>
	);
};

/* ── Areas ────────────────────────────────────────────────────────────── */

// Whose service a city is: a partner's logo with «(powered by DeliveryMan)»
// under it, or Deliveryman's own logo.
const BrandLine = ({ brandKey, small }) => {
	const brand = BRANDS[brandKey] || OWN_BRAND;
	const own = brand === OWN_BRAND;
	return (
		<span className={cn('flex flex-col gap-0.5', small ? 'items-center' : 'items-start')}>
			<img
				src={brand.logo}
				alt={brand.name}
				width={brand.width}
				height={brand.height}
				loading='lazy'
				decoding='async'
				className={cn(
					'w-auto max-w-full object-contain',
					// Deliveryman's wide wordmark needs height to stay legible.
					own ? (small ? 'h-7' : 'h-9') : cn('rounded-md', small ? 'h-8' : 'h-11'),
				)}
			/>
			{!own && (
				<span className={cn('font-medium text-(--dm-muted)', small ? 'text-[11px]' : 'text-xs')}>
					(powered by DeliveryMan)
				</span>
			)}
		</span>
	);
};

export const CityCard = ({ city, showPhone }) => {
	const brandName = (BRANDS[city.brand] || OWN_BRAND).name;
	return (
		<li className='flex items-center gap-3 rounded-2xl border border-(--dm-line) bg-white p-4 shadow-(--dm-shadow) sm:p-5'>
			<span className='inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-(--dm-blue-soft) text-(--dm-blue)'>
				<MapPin size={22} aria-hidden='true' />
			</span>
			<div className='min-w-0'>
				<p className='text-base font-bold text-(--dm-text)'>{city.name}</p>
				<div className='mt-1.5 mb-1'>
					<BrandLine brandKey={city.brand} />
				</div>
				{showPhone ? (
					<a
						href={`tel:${city.phone}`}
						className='mt-0.5 inline-flex items-center gap-1.5 text-sm font-medium text-(--dm-blue) hover:text-(--dm-navy)'
						aria-label={`Κλήση στο ${brandName} ${city.name}: ${city.display}`}
					>
						<Phone size={14} aria-hidden='true' />
						{city.display}
					</a>
				) : (
					<p className='mt-0.5 text-sm text-(--dm-muted)'>Σε λειτουργία</p>
				)}
			</div>
		</li>
	);
};

export const AreasPreview = () => (
	<section
		id='perioxes'
		aria-labelledby='perioxes-title'
		className='border-y border-(--dm-line) bg-white py-20 sm:py-28'
	>
		<Container>
			<SectionHeading
				id='perioxes-title'
				eyebrow='Περιοχές'
				title='Το DeliveryMan σε όλο και περισσότερες πόλεις.'
				text='Λειτουργούμε ήδη σε αυτές τις πόλεις και αναπτυσσόμαστε συνεχώς.'
			/>
			<Reveal as='ul' className='flex flex-wrap justify-center gap-3 sm:gap-4'>
				{CITIES.map(c => (
					<li
						key={c.name}
						className='flex min-h-14 basis-[calc(50%-6px)] flex-col items-center justify-center gap-1.5 rounded-2xl border border-(--dm-line) bg-(--dm-bg) px-3 py-3 text-center text-[15px] font-semibold text-(--dm-text) sm:basis-[180px]'
					>
						<span className='flex items-center gap-2'>
							<MapPin size={16} className='shrink-0 text-(--dm-blue)' aria-hidden='true' />
							{c.name}
						</span>
						<BrandLine brandKey={c.brand} small />
					</li>
				))}
			</Reveal>
			<div className='mt-10 flex justify-center'>
				<Link
					to='/perioxes'
					className='inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-(--dm-navy) px-6 text-[15px] font-semibold text-white transition-colors hover:bg-(--dm-navy-deep)'
				>
					Δες όλες τις περιοχές
					<ArrowRight size={18} aria-hidden='true' />
				</Link>
			</div>
		</Container>
	</section>
);

/* ── Interest from any area (expansion) ───────────────────────────────── */

const inputCls =
	'w-full min-h-12 rounded-xl border border-(--dm-line) bg-white px-4 text-base text-(--dm-text) placeholder:text-[#8a99ad] transition-colors focus:border-(--dm-blue) focus:outline-none focus:ring-4 focus:ring-(--dm-blue)/15';

const norm = s =>
	(s || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

// Greek ΑΦΜ: 9 digits with a check digit — the same rule the server applies
// to a shop's ΑΦΜ (afmOrNull in the backend's shop controller).
const isValidAfm = value => {
	const afm = String(value ?? '').replace(/\s+/g, '');
	if (!/^\d{9}$/.test(afm) || /^0+$/.test(afm)) return false;
	let sum = 0;
	for (let i = 0; i < 8; i++) sum += Number(afm[i]) * 2 ** (8 - i);
	return (sum % 11) % 10 === Number(afm[8]);
};

const InterestForm = () => {
	const { role, setRole } = useInterest();
	const [form, setForm] = useState({
		city: '',
		name: '',
		phone: '',
		email: '',
		shopName: '',
		afm: '',
		message: '',
	});
	// idle → sending → sent, or failed with the server's message (or null).
	const [state, setState] = useState({ status: 'idle', error: null });
	const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }));
	const isShop = role === 'shop';

	const activeCity = CITIES.find(c => norm(c.name) === norm(form.city));

	const onSubmit = async e => {
		e.preventDefault();
		if (state.status === 'sending') return;
		setState({ status: 'sending', error: null });
		const { shopName, afm, ...rest } = form;
		const res = await sendInterest({
			role,
			...rest,
			...(isShop ? { shopName, afm: afm.replace(/\s+/g, '') } : {}),
			// Honeypot, filled only by bots (see the hidden «website» field).
			website: e.target.elements.website?.value || '',
		});
		setState(res.ok ? { status: 'sent', error: null } : { status: 'failed', error: res.error });
	};

	if (state.status === 'sent') {
		return (
			<div
				className='flex flex-col items-center rounded-3xl border border-(--dm-line) bg-white p-8 text-center shadow-(--dm-shadow-lg) sm:p-10'
				role='status'
			>
				<span className='inline-flex h-14 w-14 items-center justify-center rounded-full bg-(--dm-blue-soft) text-(--dm-blue)'>
					<Check size={28} aria-hidden='true' />
				</span>
				<h3 className='mt-5 text-xl font-extrabold'>Ευχαριστούμε!</h3>
				<p className='mt-2 max-w-sm text-base leading-relaxed text-(--dm-muted)'>
					Λάβαμε το αίτημά σου και θα επικοινωνήσουμε μαζί σου σύντομα.
				</p>
				<Action
					variant='secondary'
					className='mt-6'
					onClick={() => {
						setForm(f => ({ ...f, message: '' }));
						setState({ status: 'idle', error: null });
					}}
				>
					Νέο αίτημα
				</Action>
			</div>
		);
	}

	return (
		<form
			onSubmit={onSubmit}
			className='relative rounded-3xl border border-(--dm-line) bg-white p-6 shadow-(--dm-shadow-lg) sm:p-8'
			aria-labelledby='form-title'
		>
			<h3 id='form-title' className='text-xl font-extrabold'>
				Εκδήλωση ενδιαφέροντος
			</h3>

			<fieldset className='mt-6'>
				<legend className='mb-2.5 text-sm font-semibold text-(--dm-text)'>Είμαι</legend>
				<div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
					{Object.entries(ROLES).map(([key, r]) => (
						<label
							key={key}
							className={cn(
								'flex min-h-12 cursor-pointer items-center justify-center rounded-xl border px-3 text-center text-[15px] font-semibold transition-colors',
								role === key
									? 'border-(--dm-blue) bg-(--dm-blue-soft) text-(--dm-navy)'
									: 'border-(--dm-line) text-(--dm-muted) hover:border-(--dm-blue)/50',
							)}
						>
							<input
								type='radio'
								name='role'
								value={key}
								checked={role === key}
								onChange={() => setRole(key)}
								className='sr-only'
							/>
							{r.label}
						</label>
					))}
				</div>
			</fieldset>

			<div className='mt-5 grid gap-4 sm:grid-cols-2'>
				<div className='sm:col-span-2'>
					<label htmlFor='dm-city' className='mb-1.5 block text-sm font-semibold'>
						Πόλη / περιοχή
					</label>
					<input
						id='dm-city'
						required
						list='dm-cities'
						autoComplete='address-level2'
						value={form.city}
						onChange={set('city')}
						placeholder='π.χ. Λάρισα'
						className={inputCls}
					/>
					<datalist id='dm-cities'>
						{CITIES.map(c => (
							<option key={c.name} value={c.name} />
						))}
					</datalist>
					{activeCity && (
						<p className='mt-2 text-sm text-(--dm-muted)' role='status'>
							Το DeliveryMan λειτουργεί ήδη στην πόλη {activeCity.name}. Μπορείς να καλέσεις και
							απευθείας στο{' '}
							<a href={`tel:${activeCity.phone}`} className='font-semibold text-(--dm-blue)'>
								{activeCity.display}
							</a>
							.
						</p>
					)}
				</div>
				{isShop && (
					<>
						<div>
							<label htmlFor='dm-shop' className='mb-1.5 block text-sm font-semibold'>
								Όνομα καταστήματος
							</label>
							<input
								id='dm-shop'
								required
								autoComplete='organization'
								value={form.shopName}
								onChange={set('shopName')}
								className={inputCls}
							/>
						</div>
						<div>
							<label htmlFor='dm-afm' className='mb-1.5 block text-sm font-semibold'>
								ΑΦΜ <span className='font-normal text-(--dm-muted)'>(προαιρετικό)</span>
							</label>
							<input
								id='dm-afm'
								inputMode='numeric'
								maxLength={11}
								value={form.afm}
								onChange={e => {
									// Optional, but one that is typed must be valid.
									const value = e.target.value.trim();
									e.target.setCustomValidity(
										!value || isValidAfm(value) ? '' : 'Το ΑΦΜ δεν είναι έγκυρο',
									);
									set('afm')(e);
								}}
								className={inputCls}
							/>
						</div>
					</>
				)}
				<div>
					<label htmlFor='dm-name' className='mb-1.5 block text-sm font-semibold'>
						Ονοματεπώνυμο
					</label>
					<input
						id='dm-name'
						required
						autoComplete='name'
						value={form.name}
						onChange={set('name')}
						className={inputCls}
					/>
				</div>
				<div>
					<label htmlFor='dm-phone' className='mb-1.5 block text-sm font-semibold'>
						Τηλέφωνο
					</label>
					<input
						id='dm-phone'
						required
						type='tel'
						inputMode='tel'
						autoComplete='tel'
						pattern='[0-9+ ()-]{10,}'
						title='Ένα τηλέφωνο τουλάχιστον 10 ψηφίων'
						value={form.phone}
						onChange={set('phone')}
						className={inputCls}
					/>
				</div>
				<div className='sm:col-span-2'>
					<label htmlFor='dm-email' className='mb-1.5 block text-sm font-semibold'>
						Email
					</label>
					<input
						id='dm-email'
						required
						type='email'
						autoComplete='email'
						value={form.email}
						onChange={set('email')}
						className={inputCls}
					/>
				</div>
				<div className='sm:col-span-2'>
					<label htmlFor='dm-message' className='mb-1.5 block text-sm font-semibold'>
						Μήνυμα <span className='font-normal text-(--dm-muted)'>(προαιρετικό)</span>
					</label>
					<textarea
						id='dm-message'
						rows={3}
						value={form.message}
						onChange={set('message')}
						className={cn(inputCls, 'py-3')}
					/>
				</div>
			</div>

			{/* Honeypot: hidden from people, filled by bots; the server drops those. */}
			<div aria-hidden='true' className='absolute -left-[9999px] h-0 w-0 overflow-hidden'>
				<label htmlFor='dm-website'>Website</label>
				<input id='dm-website' name='website' tabIndex={-1} autoComplete='off' />
			</div>

			{state.status === 'failed' && (
				<p
					className='mt-5 rounded-xl bg-(--dm-orange-soft) px-4 py-3 text-sm text-(--dm-text)'
					role='alert'
				>
					{state.error || 'Δεν στάλθηκε το αίτημα.'} Δοκίμασε ξανά ή κάλεσέ μας στο{' '}
					<a href={`tel:${CONTACT.phone}`} className='font-semibold text-(--dm-blue)'>
						{CONTACT.phoneDisplay}
					</a>
					.
				</p>
			)}

			<Action className='mt-6 w-full' type='submit' disabled={state.status === 'sending'}>
				<Send size={18} aria-hidden='true' />
				{state.status === 'sending' ? 'Αποστολή…' : 'Στείλε το ενδιαφέρον σου'}
			</Action>
			<p className='mt-3 text-center text-xs leading-relaxed text-(--dm-muted)'>
				Τα στοιχεία σου χρησιμοποιούνται μόνο για να επικοινωνήσουμε μαζί σου.
			</p>
		</form>
	);
};

export const Interest = ({ headingLevel = 'h2' }) => {
	const Heading = headingLevel;
	return (
		<section id='epikoinonia' aria-labelledby='epikoinonia-title' className='py-20 sm:py-28'>
			<Container>
				<div
					id='endiaferon'
					className='grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start lg:gap-14'
				>
					<div>
						<Eyebrow tone='orange'>Νέες περιοχές</Eyebrow>
						<Heading
							id='epikoinonia-title'
							className='mt-4 text-3xl leading-tight font-extrabold sm:text-4xl lg:text-[44px]'
						>
							Δεν υπάρχει ακόμη DeliveryMan στην πόλη σου; Πες μας.
						</Heading>
						<p className='mt-5 text-base leading-relaxed text-(--dm-muted) sm:text-lg'>
							Δεχόμαστε ενδιαφέρον από όλη την Ελλάδα. Είτε έχεις κατάστημα, είτε είσαι διανομέας,
							είτε θέλεις να στήσεις στόλο στην περιοχή σου, άφησε τα στοιχεία σου και θα
							επικοινωνήσουμε μαζί σου.
						</p>
						<ul className='mt-8 flex flex-col gap-4'>
							{[
								{ icon: Store, text: 'Καταστήματα που θέλουν διανομείς' },
								{ icon: Bike, text: 'Διανομείς που ψάχνουν δουλειά με ευελιξία' },
								{ icon: Truck, text: 'Στόλοι και συνεργάτες για νέες πόλεις' },
							].map(({ icon: Icon, text }) => (
								<li key={text} className='flex items-center gap-3 text-[15px] font-medium'>
									<span className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-(--dm-blue-soft) text-(--dm-blue)'>
										<Icon size={20} aria-hidden='true' />
									</span>
									{text}
								</li>
							))}
						</ul>
						<div className='mt-10 rounded-2xl border border-(--dm-line) bg-white p-5 shadow-(--dm-shadow)'>
							<p className='text-sm font-medium text-(--dm-muted)'>Προτιμάς να μιλήσουμε;</p>
							<a
								href={`tel:${CONTACT.phone}`}
								className='mt-1 inline-flex items-center gap-2 text-xl font-extrabold text-(--dm-navy) hover:text-(--dm-blue)'
							>
								<Phone size={20} aria-hidden='true' />
								{CONTACT.phoneDisplay}
							</a>
						</div>
					</div>
					<InterestForm />
				</div>
			</Container>
		</section>
	);
};

/* ── Final call to action ─────────────────────────────────────────────── */

export const FinalCta = () => {
	const { openInterest } = useInterest();
	const options = [
		{ role: 'shop', icon: Store, label: 'Έχω κατάστημα' },
		{ role: 'rider', icon: Bike, label: 'Είμαι διανομέας' },
		{ role: 'fleet', icon: Truck, label: 'Έχω / θέλω στόλο' },
	];
	return (
		<section aria-labelledby='final-cta-title' className='pb-20 sm:pb-28'>
			<Container>
				<Reveal className='relative overflow-hidden rounded-[2rem] bg-(--dm-navy) px-6 py-14 text-center sm:px-12 sm:py-20'>
					<div
						aria-hidden='true'
						className='pointer-events-none absolute inset-0 bg-[radial-gradient(600px_circle_at_15%_0%,rgba(22,119,255,0.35),transparent_60%),radial-gradient(500px_circle_at_100%_100%,rgba(255,107,44,0.22),transparent_60%)]'
					/>
					<div className='relative'>
						<h2
							id='final-cta-title'
							className='mx-auto max-w-3xl text-3xl leading-tight font-extrabold text-white! sm:text-5xl'
						>
							Έτοιμος να πάμε το delivery στην επόμενη μέρα;
						</h2>
						<p className='mx-auto mt-5 max-w-xl text-base text-white/75 sm:text-lg'>
							Βρες τον τρόπο συνεργασίας που σου ταιριάζει.
						</p>
						<div className='mx-auto mt-10 grid max-w-3xl gap-3 sm:grid-cols-3'>
							{options.map((o, i) => (
								<Action
									key={o.role}
									href='#endiaferon'
									onClick={e => {
										e.preventDefault();
										openInterest(o.role);
									}}
									variant={i === 0 ? 'primary' : 'ghostLight'}
									className='min-h-14'
								>
									<o.icon size={18} aria-hidden='true' />
									{o.label}
								</Action>
							))}
						</div>
					</div>
				</Reveal>
			</Container>
		</section>
	);
};
